import { useOidcContext } from '@uag/react-core';
import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CopilotConversationMenu } from 'app/modules/wallforming/components/copilot/CopilotConversationMenu';
import { CopilotMessageList } from 'app/modules/wallforming/components/copilot/CopilotMessageList';
import { CopilotPromptComposer } from 'app/modules/wallforming/components/copilot/CopilotPromptComposer';
import { useCopilotConversationManager } from 'app/modules/wallforming/components/copilot/hooks/useCopilotConversationManager';
import { useCopilotMessageClipboard } from 'app/modules/wallforming/components/copilot/hooks/useCopilotMessageClipboard';
import { useCopilotMessageSender } from 'app/modules/wallforming/components/copilot/hooks/useCopilotMessageSender';
import { useCopilotModel } from 'app/modules/wallforming/components/copilot/hooks/useCopilotModel';
import { useCopilotProposalHandler } from 'app/modules/wallforming/components/copilot/hooks/useCopilotProposalHandler';
import { useCopilotRequestBuilder } from 'app/modules/wallforming/components/copilot/hooks/useCopilotRequestBuilder';
import {
    buildConversationHistory,
    getImageResourceBySize
} from 'app/modules/wallforming/components/copilot/utils/copilotUtils';
import { EMPTY_GUID } from 'shared/constants';

import type { CopilotMode, CopilotPanelProps } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

const MAX_HISTORY_MESSAGES = 8;
const MAX_HISTORY_CHARACTERS = 4_000;
const MAX_HISTORY_MESSAGE_CHARACTERS = 2_000;

export const CopilotPanel = ({ isOpen, onClose }: CopilotPanelProps) => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('formingsession')?.trim() || EMPTY_GUID;
    const { user } = useOidcContext();
    const [prompt, setPrompt] = useState('');
    const [mode, setMode] = useState<CopilotMode>('ask');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const applyingRef = useRef(false);

    const {
        activeConversation,
        chatState,
        createConversation,
        deleteConversation,
        messages,
        rejectProposal,
        renameConversation,
        selectConversation,
        setChatState,
        updateProposal
    } = useCopilotConversationManager(isSending, applyingRef, setPrompt, setError);
    const { isApplying, applyProposal } = useCopilotProposalHandler({
        applyingRef,
        onClose,
        sessionId,
        setError,
        updateProposal
    });
    const modelName = useCopilotModel(setError);
    const copyMessage = useCopilotMessageClipboard(setError);
    const conversationHistory = useMemo(
        () =>
            buildConversationHistory(
                messages,
                MAX_HISTORY_MESSAGES,
                MAX_HISTORY_CHARACTERS,
                MAX_HISTORY_MESSAGE_CHARACTERS
            ),
        [messages]
    );
    const buildRequest = useCopilotRequestBuilder({ conversationHistory, mode, sessionId });
    const { cancelMessage, sendMessage } = useCopilotMessageSender({
        activeConversation,
        buildRequest,
        isOpen,
        isSending,
        prompt,
        setChatState,
        setError,
        setIsSending,
        setPrompt
    });
    const imageSmall = useMemo(() => (user ? getImageResourceBySize(user, 's') : undefined), [user]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <CopilotConversationMenu
                activeConversationId={chatState.activeConversationId}
                conversations={chatState.conversations}
                isApplying={isApplying}
                isSending={isSending}
                onCreate={createConversation}
                onDelete={deleteConversation}
                onRename={renameConversation}
                onSelect={selectConversation}
            />
            <CopilotMessageList
                activeConversationId={activeConversation?.id}
                imageSmall={imageSmall}
                isApplying={isApplying}
                isSending={isSending}
                messages={messages}
                mode={mode}
                onApplyProposal={applyProposal}
                onCopyMessage={copyMessage}
                onRejectProposal={rejectProposal}
            />
            <CopilotPromptComposer
                error={error}
                isSending={isSending}
                mode={mode}
                modelName={modelName}
                prompt={prompt}
                onCancel={cancelMessage}
                onModeChange={setMode}
                onPromptChange={setPrompt}
                onSend={() => void sendMessage()}
            />
        </div>
    );
};
